/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { inject } from '@angular/core';
import { of } from 'rxjs';
import { RequestCategoriesService } from '@src/app/modules/crm/request-categories.service';

import { Field } from '@src/app/shared/field/Field';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { FieldSet, LAYOUT_OPTIONS } from '@src/app/shared/form/field-set/field-set.component';
import { FormConfig } from '@src/app/shared/form/FormConfig';
import { PicklistField } from '@src/app/shared/field/PicklistField';
import { FormComboBoxComponent } from '@src/app/shared/form/form-combo-box/form-combo-box.component';
import { FormDateTimeComponent } from '@src/app/shared/form/form-date-time/form-date-time.component';
import { FormNumberComponent } from '@src/app/shared/form/form-number/form-number.component';
import { FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { FormTextAreaComponent, TextAreaField } from '@src/app/shared/form/form-text-area/form-text-area.component';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { required } from '@src/app/shared/validators';
import { AbstractHttpService } from '@src/app/shared/abstract-http.service';
import { FormPickLinkComponent } from '@src/app/shared/form/form-pick-link/form-pick-link.component';
import { SrClass } from '@src/app/model/sr-class';
import { Area } from '@src/app/model/area';
import { AiLog } from '@src/app/model/ai-log';
import { AbstractObject, TenantId, uuid } from '@src/app/model/abstract-object';
import { Action } from '@src/app/model/action';
import { BCode } from '@src/app/model/bcode';
import { Schedule } from '@src/app/model/schedule';
import { GridField } from '@src/app/shared/grid/grid-field';
import { Attachment } from '@src/app/model/attachment';
import { MAIL_STATUS, Person } from '@src/app/model/person';
import { RequestTemplate } from '@src/app/model/RequestTemplate';
import { Txn } from '@src/app/model/txn';

import { Team } from '@src/app/model/team';
import { Omc } from '@src/app/model/Omc';
import { Unit } from '@src/app/model/unit';
import { Follower, User } from '@src/app/model/user';
import { DateHelper } from '@src/app/shared/dateHelper';
import { Memo } from '@src/app/model/memo';
import { PreferredSupplier, Supplier } from '@src/app/model/supplier';
import { MailItem } from '@src/app/model/mailMerge';
import { FormPillComponent, PillColor, PillStyle } from '@src/app/shared/form/form-pill/form-pill.component';
import { PreferredSupplierPageNavRoute } from '@src/app/routes/preferred-supplier-page-nav';
import { ServiceRequestPageNavRoute } from '@src/app/routes/service-request-page-nav';
import { PersonPageNavRoute } from '@src/app/routes/person-page-nav';
import { UnitPageNavRoute } from '@src/app/routes/unit-page';
import { MyInjector } from '@src/app/app.module';
import { makeItemOwnerField } from '@src/app/shared/field/ItemOwnerField';
import { UnitService } from '@src/app/modules/unit/unit.service';
import { PersonService } from '@src/app/pages/person-page/person.service';
import { SupplierService } from '@src/app/modules/supply/supplier.service';

export class RequestBudget extends AbstractObject {
    bCodeId!: uuid;
    scheduleId!: uuid;
    amount = 0;
    bCodeName?: string; // server-joined budget-code description, read-only display
}

/** A ballot_srv_req link row - id is the link's own, ballotId names the ballot. */
export class BallotRequest extends AbstractObject {
    ballotId!: uuid;
    requestId!: uuid;
    ballotTitle?: string;
    ballotOpensAt?: number;
    ballotClosesAt?: number;
}
/**
 * A picklist item that can also render as a pill (FormPillComponent) - plain picklists ignore the
 * extras. No `style` means no pill: the value shows as plain text.
 */
export class SrPillItem extends AbstractObject {
    style?: PillStyle;
    color?: PillColor;
    icon?: string;
    tip?: string;
}

export class SrState extends SrPillItem {
    sortOrder: number;
}

export class BillingState extends AbstractObject {

}
export class ServiceRequest extends AbstractObject {
    title: string;
    description: string;
    srStatus: uuid;
    subStatus: uuid;
    srCloseStatus: uuid;
    completedAt?: number;
    resolvedById?: uuid;
    resolvedByName?: string;
    ownerReadAt: number;

    agentTeamId: uuid;

    reportedByPersonId: uuid;
    reportedByName: string;

    ownedById: uuid;
    ownedBy?: User;
    /** Flat name selected by the owner join - what makeItemOwnerField renders. */
    ownedByName?: string;

    actions: Action[] = [];
    attachments: Attachment[] = [];
    memos?: Memo[] = [];
    mailItems?: MailItem[] = [];

    templateId: uuid;
    template: RequestTemplate;
    data: unknown;
    dataFields?: AbstractObject[] = [];
    requestBudget?: RequestBudget[] = [];
    /** Purchases and accruals raised against this work order - the actual, against the budget above. */
    transactions?: Txn[] = [];
    childRequests?: ServiceRequest[] = [];
    ballots?: BallotRequest[] = [];
    followers: Follower[] = [];

    supplierId?: uuid;
    supplier?: Supplier;

    // Derived by the database from supplierId - see getRequestTypeField. Read only; never posted.
    requestType?: string;

    // Client only - what the request page heard back from the assistant, see makeAssistantPillField.
    aiStatus?: string;
    aiMessage?: string;
    aiLog?: AiLog;

    forumId?: uuid;
    forumName?: string;
    fullForumName?: string;
    forumPillColor?: string;
    forumPillIcon?: string;

    // Flat names from extraPeers joins - the OMC (srv_reqs.teamId) and its handling agency
    teamName?: string;
    agentName?: string;

    refNr?: number;

    categoryId: uuid;
    priorityId: uuid;


    plannedDate: string;

    omcId: uuid;
    omc: Omc;

    jobId: uuid;

    parentId: uuid;
    parent: ServiceRequest;

    team: Team;

    unitId: uuid;
    unit: Unit;

    personId: uuid;
    person: Person;

    approvedAt: number;
    approvedById: uuid;
    approvedByName: string;

    openMonth: string;

    requestSource: string;

    // Classification - ids owned by the server (SrTopic / SrClass reference data).
    srTopic: string;
    srClass: string;

    // Where in the development - null unless the request itself makes the location plain.
    // The names are joined on by ServiceRequest::extraPeers, not eager loaded children.
    areaId: uuid;
    coreId: uuid;
    areaName: string;
    coreName: string;

    constructor(o: Partial<ServiceRequest> = {}) {
        super(o);
    }

    static srOpen = { id: 'open', name: 'Open', style: 'outline', color: 'neutral', icon: 'folder_open' } as SrState;
    static srResolved = { id: 'resolved', name: 'Resolved', style: 'infill', color: 'green', icon: 'check' } as SrState;
    static srAccepted = { id: 'accepted', name: 'Accepted', style: 'infill', color: 'darkGreen', icon: 'lock' } as SrState;

    static srStates: SrState[] = [ServiceRequest.srOpen, ServiceRequest.srResolved, ServiceRequest.srAccepted];

    static billOmc = {id: 'omc', name: 'To Be Billed To OMC'} as BillingState
    static billOwner = { id: 'owner', name: 'To Be Billed To Owner' } as BillingState
    static billedOmc = { id: 'billedOmc', name: 'Billed To OMC' } as BillingState
    static billedOwner = { id: 'billedOwner', name: 'Billed To Owner' } as BillingState
    static included = { id: 'included', name: 'Included in SLA' } as BillingState
    static billingStates: BillingState[] = [
        ServiceRequest.billOmc, ServiceRequest.billOwner, ServiceRequest.billedOmc,
        ServiceRequest.billedOwner, ServiceRequest.included
    ];


    static srOverdue = {
        id: 'overdue', name: 'Overdue', style: 'infill', color: 'red', icon: 'alarm',
        tip: $localize`There are planned actions that are past their due date, either complete the action or update the planned date on them`
    } as SrState;
    static srPlanned = {
        id: 'planned', name: 'Planned', style: 'outline', color: 'green', icon: 'event_available',
        tip: $localize`There are planned actions to progress this request`
    } as SrState;
    static srUnplanned = {
        id: 'unplanned', name: 'Unplanned', style: 'infill', color: 'amber', icon: 'edit_calendar',
        tip: $localize`Either close this request, or create actions with your plan of how the request should be dealt with`
    } as SrState;
    /*
     * The close states are neutral rather than colourless: a classless pill would now render as
     * plain text, and these are clickable (they open the action grid), so they need to keep
     * looking like something you can press.
     */
    static srComplete = {
        id: 'complete', name: 'Complete', style: 'infill', color: 'neutral', icon: 'done_all',
        tip: $localize`All work has been completed for this request`
    } as SrState;
    static srDuplicate = {
        id: 'duplicate', name: 'Duplicate', style: 'infill', color: 'neutral', icon: 'content_copy',
        tip: $localize`Request closed as duplicate of another request`
    } as SrState;
    static srCancelled = {
        id: 'cancelled', name: 'Cancelled', style: 'infill', color: 'neutral', icon: 'cancel',
        tip: $localize`This request has been cancelled by the requester`
    } as SrState;
    static srNoAction = {
        id: 'noAction', name: 'No Action', style: 'infill', color: 'neutral', icon: 'block',
        tip: $localize`No further action was required on this request`
    } as SrState;

    static srActionStates = [ServiceRequest.srOverdue, ServiceRequest.srPlanned,
        ServiceRequest.srUnplanned];

    static srCloseStates = [ServiceRequest.srComplete, ServiceRequest.srDuplicate,
        ServiceRequest.srCancelled, ServiceRequest.srNoAction
    ];

    static srSubStates = ServiceRequest.srActionStates.concat(ServiceRequest.srCloseStates)

    // How a request came into the system. 'unknown' is the legacy default and is not user-pickable.
    static srcEmailAuto = { id: 'emailAuto', name: $localize`Email to system` };
    static srcEmailManual = { id: 'emailManual', name: $localize`Email to office` };
    static srcPhone = { id: 'phone', name: $localize`Phone call` };
    static srcWalkIn = { id: 'walkIn', name: $localize`Visit to office` };
    static srcPaper = { id: 'paper', name: $localize`Mail or Fax` };
    static srcMeeting = { id: 'meeting', name: $localize`Meeting` };
    static srcSiteVisit = { id: 'siteVisit', name: $localize`Site Visit` };
    static srcOoHours = { id: 'ooHours', name: $localize`Out Of Hours Service` };
    static srcApp = { id: 'app', name: $localize`Online User` };
    static srcQuickRequest = { id: 'quickRequest', name: $localize`App Quick Request` };
    // Triggers rather than channels - reactive work against an existing request, and planned work
    // the system raised on a schedule.
    static srcResponsive = { id: 'responsive', name: $localize`Responding to request` };
    static srcRecurring = { id: 'recurring', name: $localize`Recurring Job` };
    static srcSystem = { id: 'system', name: $localize`Raised by system` };
    static srcUnknown = { id: 'unknown', name: $localize`Unknown` };

    // Full list (for read-only display of any value). Order matches the server enum.
    static requestSources = [
        ServiceRequest.srcEmailAuto, ServiceRequest.srcEmailManual, ServiceRequest.srcPhone,
        ServiceRequest.srcWalkIn, ServiceRequest.srcPaper, ServiceRequest.srcMeeting,
        ServiceRequest.srcSiteVisit, ServiceRequest.srcOoHours, ServiceRequest.srcApp,
        ServiceRequest.srcQuickRequest, ServiceRequest.srcResponsive, ServiceRequest.srcRecurring,
        ServiceRequest.srcSystem, ServiceRequest.srcUnknown
    ];

    // Subset an agent may choose when logging a request manually.
    static pickableSources = [
        ServiceRequest.srcEmailManual, ServiceRequest.srcPhone, ServiceRequest.srcWalkIn,
        ServiceRequest.srcPaper, ServiceRequest.srcMeeting, ServiceRequest.srcSiteVisit,
        ServiceRequest.srcOoHours
    ];

    // Derived by the server from supplierId - a request with a supplier is a Work Order, anything
    // else is a Case. Matches the server enum; never sent back.
    // Outline pills, not infill: the type is an attribute of the request, not a state it is in,
    // so it must not borrow the traffic-light vocabulary the status pills own. Case and Work Order
    // are siblings in the same treatment, separated by hue - the 15% case is the one that stands out.
    static typeCase = {
        id: 'case', name: $localize`Case`,
        style: 'outline', color: 'neutral', icon: 'live_help'
    } as SrPillItem;
    static typeWorkOrder = {
        id: 'workOrder', name: $localize`Work Order`,
        style: 'outline', color: 'blue', icon: 'construction'
    } as SrPillItem;
    static requestTypes = [ServiceRequest.typeCase, ServiceRequest.typeWorkOrder];

    // Read-only condensed display of the request type.
    static getRequestTypeField() {
        const field = FormPillComponent.make('Type', 'requestType',
            { states: ServiceRequest.requestTypes, tableIconOnly: true }).override({ formColumn: 4 });
        field.cellOpts.heading = '';
        field.toolTip = $localize`Request type`;
        return field;
    }

    // Read-only condensed display of how the request was logged (any value).
    static getSourceField() {
        return FormPicklistComponent.make('Source', 'requestSource', null,
            { items: ServiceRequest.requestSources, allowSelectNone: false },
            { formColumn: 4, readonly: true, disable: true, sendServer: false, condensed: true }
        );
    }

    // Required picklist for an agent to record how a request was logged (manual subset).
    // The items are a copy: PicklistField holds the array by reference, so a caller adding an option
    // (as the work order stepper does with 'responsive') would otherwise edit the static list itself.
    static getSourcePicklist() {
        return FormPicklistComponent.make('Source', 'requestSource', null,
            { items: [...ServiceRequest.pickableSources], allowSelectNone: false },
            { validators: [required] }
        );
    }

    static getOpenMonthField() {
        return FormTextComponent.make('Month Opened', 'openMonth', { visible: Field.noShow, sendServer: false, readonly: true });
    }
    static getOpenWeekField() {
        return FormTextComponent.make('Week Opened', 'openWeek', { visible: Field.noShow, sendServer: false, readonly: true });
    }
    static getCloseMonthField() {
        return FormTextComponent.make('Month Closed', 'closeMonth', { visible: Field.noShow, sendServer: false, readonly: true });
    }
    static getCloseWeekField() {
        return FormTextComponent.make('Month Opened', 'closeWeek', { visible: Field.noShow, sendServer: false, readonly: true });
    }

    static getRefField() {
        return FormNumberComponent.make('Reference', 'refNr', { format: 'plain' },
            { disable: true, formColumn: 4, cellOpts: { heading: 'Ref' } }
        );
        //return FieldMaker.id({ label: 'Reference', cellOpts: { heading: 'Ref' }, visible: Field.showAll, disable: true, formColumn: 3 });
    }
    static getTemplateIdField() {
        return FieldMaker.idHolder('templateId');
    }

    /** Columns for the parent request picker */
    static pickDialogFormConfig(): FormConfig {
        return new FormConfig({
            fieldSet: new FieldSet({
                fields: [
                    FormNumberComponent.make('Ref', 'refNr', { format: 'plain' }),
                    FormTextComponent.make('Title', 'title'),
                    FormTextComponent.make('Status', 'subStatus'),
                ],
                formLayout: LAYOUT_OPTIONS.singleCol,
            }),
            title: $localize`Open Requests`,
            mode: 'list',
        });
    }

    static getOmcField(): PicklistField {
        return FormPicklistComponent.make('Owner Management Company', 'omcId', 'omc',
            { items: [] }, { validators: [required] })
    }
    static getAgentField(): PicklistField {
        return FormPicklistComponent.make('Agent', 'agentTeamId', 'agentTeam',
            { items: [] }, { validators: [required] })
    }

    static getTitleField() {
        return FormTextComponent.make('Title - A shortname for this request', 'title', { cellOpts: { heading: 'Title' }, validators: [required] });
    }

    static getDescriptionField(options: Partial<TextAreaField> = {}) {
        return FormTextAreaComponent.make('Detailed description of what is needed', 'description',
            { cellOpts: { heading: 'Description' }, validators: [required], visible: Field.formOnly, ...options }
        );
    }

    static getSupplierField() {
        return FormComboBoxComponent.make('Supplier', 'supplierId', 'supplier', {
            items: [], allowSelectNone: true, detailView: PreferredSupplierPageNavRoute,
            optionDisplayValue: (o: PreferredSupplier) => o ? o.name + (o.code ? ' (' + o.code + ')' : '') : ''
        }, {
            formColumn: 1,
        });
    }

    static getParentField() {
        return FormPicklistComponent.make('Parent', 'parentId', 'parent', {
            items: [], allowSelectNone: true, detailView: ServiceRequestPageNavRoute,
            optionDisplayValue: o => o.refNr ? (o.refNr + ':' + o.title) : '',
        }, {
            formColumn: 1,
        });
    }

    static getReportedByField() {
        return FormPicklistComponent.make('Reported By', 'reportedByPersonId', 'reportedByName', { selfPopulate: true }, { 
            formColumn: 4, disable: true, hint: $localize `Person who created this request in system` });
    }
    static getApprovedByField() {
        return FormTextComponent.make('Approved By', 'approvedByName', {
            formColumn: 4, condensed: true, readonly: true, disable: true, sendServer: false,
            visible: Field.formOnly, docAlwaysInclude: true });
    }
    static getResolvedByField() {
        return FormTextComponent.make('Resolved By', 'resolvedByName', {
            formColumn: 4, condensed: true, readonly: true, disable: true, sendServer: false,
            visible: Field.formOnly, docAlwaysInclude: true,
            hint: $localize `User who closed this request` });
    }
    static getResolvedBySearchField() {
        return FormPicklistComponent.make('Resolved By', 'resolvedById', 'resolvedByName', { selfPopulate: true }, {
            visible: Field.noShow, disable: true, hint: $localize `User who closed this request` });
    }
    static getApprovedBySearchField() {
        return FormPicklistComponent.make('Approved By', 'approvedById', 'approvedByName', { selfPopulate: true }, { 
            visible: Field.noShow, disable: true, hint: $localize `Person who approved this request` });
    }    
    static getBillableField() {
        return FormPicklistComponent.make('Billable', 'billingStatus', null,
            { items: ServiceRequest.billingStates, allowSelectNone: true }
        );
    }

    static getCategoryField() {
        return FormPicklistComponent.make('Category', 'categoryId', 'category', {
            service: inject(RequestCategoriesService)
        });
    }

    static makeCategoryField() {
        return FormPicklistComponent.make('Category', 'categoryId', 'category', { items: [] });
    }

    /*
     * Area + Core travel together the same way topic and class do - the core list is the cores of
     * the chosen area. `areaValue` / `coreValue` name the fields, because the request page calls
     * them areaId / coreId and an AI answer calls them srArea / srCore. Call the returned
     * `narrowCores` once the areas have loaded to seed the list for the record's own area.
     */
    static makeAreaAndCoreFields(getAreas: () => Area[], areaValue = 'areaId', coreValue = 'coreId') {
        const areaField = FormPicklistComponent.make($localize`Area`, areaValue, null,
            { items: [], allowSelectNone: true });
        const coreField = FormPicklistComponent.make($localize`Core`, coreValue, null,
            { items: [], allowSelectNone: true });

        const narrowCores = (areaId: uuid | null) => {
            const area = getAreas().find(a => a.id === areaId);
            const list = area?.cores ?? [];
            coreField.setPicklistItems(list);
            const ctl = coreField.control;
            if (ctl?.value && !list.some(c => c.id === ctl.value)) {
                ctl.setValue(null);
            }
        };
        areaField.picklist.refreshes.push((a: AbstractObject) => narrowCores((a?.['id'] as uuid) ?? null));

        return { areaField, coreField, narrowCores };
    }

    static classesForTopic(all: SrClass[], topicId: uuid | null): SrClass[] {
        return topicId ? all.filter(c => c.srTopic === topicId) : all;
    }

    /*
     * Topic + Class always travel together: the class list is the classes of the chosen topic, and
     * changing topic clears a class that no longer fits. Baked in here so every caller (page rail,
     * details dialog) gets it. `getClasses` supplies the full class list, which loads async; call
     * the returned `narrowClasses` once after the record loads to seed the list for its topic.
     */
    static makeTopicAndClassFields(getClasses: () => SrClass[]) {
        const topicField = FormPicklistComponent.make('Topic', 'srTopic', null,
            { items: [], allowSelectNone: false }, { validators: [required] });
        const classField = FormPicklistComponent.make('Class', 'srClass', null,
            { items: [], allowSelectNone: false }, { validators: [required] });

        const narrowClasses = (topicId: uuid | null) => {
            const list = ServiceRequest.classesForTopic(getClasses(), topicId);
            classField.setPicklistItems(list);
            const ctl = classField.control;
            if (ctl?.value && !list.some(c => c.id === ctl.value)) {
                ctl.setValue(null);
            }
        };
        topicField.picklist.refreshes.push((t: AbstractObject) => narrowClasses((t?.['id'] as uuid) ?? null));

        return { topicField, classField, narrowClasses };
    }

    static makeParentField(svc: AbstractHttpService) {
        return FormPickLinkComponent.make({
            label: 'Parent',
            value: 'parentId',
            textValue: (o: ServiceRequest) => o.parent?.title,
            nav: ServiceRequestPageNavRoute,
            pick: {
                service: svc,
                config: () => ServiceRequest.pickDialogFormConfig(),
                filter: (o: ServiceRequest) => ({
                    srStatus: ServiceRequest.srOpen.id, omcId: o.omcId, parentId: 'NULL', notId: o.id,
                }),
                forceTeamId: (o: ServiceRequest) => o.teamId,
                textFrom: (sr: ServiceRequest) => sr.title,
                allowNull: true,
            },
        });
    }

    /*
     * People pick-links, shared by the page rail (then .override({ condensed, formColumn })) and
     * the "Edit the people" dialog (raw). Each pulls its own (only sensible) service via MyInjector
     * so the factory works from a plain class too, not just an Angular injection context.
     */
    static makeOwnedByField() {
        return makeItemOwnerField();
    }

    /*
     * Unit + Contact always travel together: a contact is only meaningful against its unit, so
     * picking a new unit clears the contact. Baked in here so both callers get it.
     */
    static makeUnitAndContactFields() {
        const unitField = FormPickLinkComponent.make({
            label: 'Unit',
            value: 'unitId',
            textValue: (o: ServiceRequest) => Unit.getFullName(o.unit),
            nav: UnitPageNavRoute,
            pick: {
                service: MyInjector.instance.get(UnitService),
                config: () => Unit.pickDialogFormConfig(),
                forceTeamId: (o: ServiceRequest) => o.teamId,
                textFrom: (u: Unit) => Unit.getFullName(u),
                allowNull: true,
            },
        });
        const personField = FormPickLinkComponent.make({
            label: 'Contact',
            value: 'personId',
            textValue: 'person.fullName',
            nav: PersonPageNavRoute,
            pick: {
                service: MyInjector.instance.get(PersonService),
                config: () => Person.pickDialogFormConfig(),
                // narrowed to whatever unit is currently on the form
                filter: (o: ServiceRequest) => o.unitId ? { unitId: o.unitId } : {},
                forceTeamId: (o: ServiceRequest) => o.teamId,
                textFrom: (p: Person) => p.fullName,
                allowNull: true,
            },
        });
        unitField.refreshes.push(() => personField.clear());
        const contactLinkField = ServiceRequest.makeContactLinkField();
        personField.refreshes.push((picked: Person) => ServiceRequest.offerContactLink(contactLinkField, picked));
        return { unitField, personField, contactLinkField };
    }

    static readonly contactFollow = 'follow';
    static readonly contactLinkOnly = 'linkOnly';

    /*
     * Whether the contact just chosen also follows the request. Never stored: the server acts on it
     * for the create, or for the put that changes the contact, and forgets it. Hidden until there
     * is a contact to decide about - see offerContactLink.
     */
    static makeContactLinkField() {
        return FormPicklistComponent.make($localize`Keep Contact Informed`, 'contactLink', null,
            { items: [], allowSelectNone: false },
            { visible: Field.noShow }
        );
    }

    /**
     * A contact who can be mailed is followed unless the user says otherwise; one who cannot is
     * only linked, and the single option on offer says why.
     */
    static offerContactLink(field: PicklistField, contact: Person) {
        if (!contact) {
            field.visible = Field.noShow;
            field.control.setValue(null);
            return;
        }
        const blockedReason = ServiceRequest.whyContactCannotBeMailed(contact);
        field.visible = Field.formOnly;
        field.setPicklistItems(ServiceRequest.contactLinkItems(blockedReason));
        field.hint = ServiceRequest.contactLinkHint(contact, blockedReason);
        field.control.setValue(blockedReason ? ServiceRequest.contactLinkOnly : ServiceRequest.contactFollow);
    }

    private static whyContactCannotBeMailed(contact: Person): string {
        if (!contact.email) {
            return $localize`no email address`;
        }
        if (contact.mailStatus && contact.mailStatus !== MAIL_STATUS.ok) {
            return MAIL_STATUS.getDescription(contact.mailStatus).toLowerCase();
        }
        return '';
    }

    private static contactLinkItems(blockedReason: string): AbstractObject[] {
        if (blockedReason) {
            return [{ id: ServiceRequest.contactLinkOnly, name: $localize`Link Only (${blockedReason})` }];
        }
        return [
            { id: ServiceRequest.contactFollow, name: $localize`Follow` },
            { id: ServiceRequest.contactLinkOnly, name: $localize`Link Only` },
        ];
    }

    private static contactLinkHint(contact: Person, blockedReason: string): string {
        if (blockedReason) {
            return $localize`${contact.fullName} cannot be mailed (${blockedReason}), so they are linked
                to the request but will not be kept informed about it.`;
        }
        return $localize`Follow adds ${contact.fullName} as a follower, so they are mailed as the request
            progresses - new comments, and when it is closed or reopened. Link Only connects them to the
            request without ever mailing them about it.`;
    }

    /**
     * The money behind a work order - one row per budget code / schedule / amount. The bCode and
     * schedule lists are OMC-specific and load async, so they are held by reference: the caller
     * fills the arrays (AgentDataService.getOmcExpenseBCodes / getOmcSchedules) and the picklists
     * pick them up. `teamId` is read per row rather than captured, because the request's team is
     * not always known when the field is built.
     */
    static makeBudgetGridField(bCodes: BCode[], schedules: Schedule[], teamId: () => TenantId) {
        const bcf = FormPicklistComponent.make('Budget Code', 'bCodeId', null, { items: bCodes }, { validators: [required] });
        bcf.cellOpts.style = "background-color: '#f0f0f0', fontWeight: 'bold'";
        return new GridField({
            field: { label: 'Budget', value: 'requestBudget' },
            rowFactory: () => [
                FieldMaker.id(),
                FieldMaker.rev(),
                FieldMaker.idHolder('teamId'),
                bcf,
                FormPicklistComponent.make('Schedule', 'scheduleId', null, { items: schedules }, { validators: [required] }),
                FormNumberComponent.makeCurrency('Amount', 'amount'),
                FieldMaker.deleteGridRow(),
            ],
            objFactory: () => {
                const item = new RequestBudget();
                item.teamId = teamId();
                if (schedules.length === 1) {
                    item.scheduleId = schedules[0].id;
                }
                return of(item);
            },
        });
    }

    /** The supplier of the work. Shared by the page rail and the Work Order edit dialog. */
    static makeSupplierField() {
        return FormPickLinkComponent.make({
            label: 'Supplier',
            value: 'supplierId',
            textValue: (o: ServiceRequest) => o.supplier?.name,
            nav: PreferredSupplierPageNavRoute,
            pick: {
                service: MyInjector.instance.get(SupplierService),
                config: () => Supplier.pickDialogFormConfig(),
                forceTeamId: (o: ServiceRequest) => o.teamId,
                textFrom: (s: Supplier) => s.name,
                // No clear icon: removing the supplier would turn a Work Order back into a Case
                // Request, and requestType is fixed at creation.
                allowNull: false,
            },
        });
    }

    // Fixed priority levels, matches the server SrvReqPrio enum. Not user-editable - relabel here if needed.
    // Normal carries no style, so the overwhelmingly common value shows as plain text and the two
    // that want attention are the only pills in the column.
    static prioNormal = { id: 'normal', name: $localize`Normal` } as SrPillItem;
    static prioLow = {
        id: 'low', name: $localize`Low`, style: 'outline', color: 'grey', icon: 'arrow_downward'
    } as SrPillItem;
    static prioHigh = {
        id: 'high', name: $localize`High`, style: 'outline', color: 'amber', icon: 'arrow_circle_up'
    } as SrPillItem;
    static prioCritical = {
        id: 'critical', name: $localize`Critical`, style: 'outline', color: 'red',
        icon: 'warning'
    } as SrPillItem;
    static priorities = [
        ServiceRequest.prioNormal, ServiceRequest.prioLow, ServiceRequest.prioHigh, ServiceRequest.prioCritical
    ];

    /** The editable picklist - new request, templates, scheduled jobs, the details edit dialog. */
    static getPriorityField() {
        return FormPicklistComponent.make('Priority', 'priorityId', null,
            { items: ServiceRequest.priorities, allowSelectNone: false }
        );
    }

    /** Read-only pill for display contexts - the request page rails and the requests table. */
    static getPriorityPillField() {
        const field = FormPillComponent.make('Priority', 'priorityId',
            { states: ServiceRequest.priorities, tableIconOnly: true });
        field.cellOpts.heading = '';
        field.toolTip = $localize`Priority`;
        return field;
    }

    static getOwnedByField(users: User[]) {
        return FormComboBoxComponent.make('Owned By', 'ownedById', 'ownedBy', { items: users }, { formColumn: 3 });
    }
    /** The same owner field, read only - it still links to what that owner has open. */
    static getOwnedByROField() {
        return makeItemOwnerField({ cellOpts: { heading: 'Owned By' }, readonly: true, sendServer: false });
    }

    static getStatusField() {
        return FormPicklistComponent.make('Status', 'srStatus', null,
            { items: ServiceRequest.srStates, allowSelectNone: false },
            { readonly: true, formColumn: 3, sendServer: false }
        );
    }

    static getSubStatusField() {
        return FormPicklistComponent.make('Action Status', 'subStatus', null,
            { items: ServiceRequest.srSubStates, allowSelectNone: false },
            { readonly: true, formColumn: 3, sendServer: false }
        );
    }

    static getStateField() {
        return FormPicklistComponent.make('Status', 'srStatus', null,
            { items: ServiceRequest.srStates, allowSelectNone: false }, { readonly: true, formColumn: 4, sendServer: false }
        );
    }

    static getCreatedField() {
        return FormDateTimeComponent.make('Created', 'createdAt', { readonly: true, disable: true, formColumn: 4 });
    }

    static getOwnerReadAtField() {
        return FormDateTimeComponent.make('Owner Read', 'ownerReadAt', {
            readonly: true, disable: true, sendServer: false, formColumn: 4, hint: 'Date read by owner', condensed: true
        });
    }

    static getCompletedField() {
        return FormDateTimeComponent.make('Completed', 'completedAt', { 
            readonly: true, disable: true, formColumn: 4, condensed: true, hint: $localize `Date reqiuest marked completed` });
    }
    static getApprovedAtField() {
        return FormDateTimeComponent.make('Approved', 'approvedAt', {
            readonly: true, disable: true, formColumn: 4, condensed: true, docAlwaysInclude: true,
            hint : $localize `Date this request approved to be carried out` });
    }

    static getLastUpdatedField() {
        return FormDateTimeComponent.make('Last Updated', 'updatedAt', { readonly: true, disable: true, formColumn: 4 });
    }

    /**
     * To be decommissioned. Builds a whole ServiceRequest from a template and clamps any action date
     * that falls in the past to today, hiding the problem rather than making the user deal with it.
     * NewRequestStepperComponent.applyTemplate replaces it and leaves past dates alone for its
     * validator to catch. ScheduleJobsStepperComponent is the last caller.
     */
    static templateMapper(template: RequestTemplate, sr: ServiceRequest, user: User): ServiceRequest {

        if (template) {
            sr.title = template.name;
            sr.description = template.description;
            sr.templateId = template.id;
            sr.categoryId = template.categoryId;
            sr.priorityId = template.priorityId;
            if (template.srTopic) {
                sr.srTopic = template.srTopic;
                sr.srClass = template.srClass;
            }

            sr.ownedById = sr.omc ? Omc.getUserId(sr.omc, template.ownerOmcRole) : user.id;
            if (!sr.ownedById) {
                sr.ownedById = user.id;
            }

            for (const actionTemplate of template.actions.sort((a, b) => a.offset - b.offset)) {
                const ownerId = sr.omc ? Omc.getUserId(sr.omc, actionTemplate.ownerOmcRole) : null;

                const newAction = new Action({
                    title: actionTemplate.name,
                    templateId: actionTemplate.id,
                    template: actionTemplate,
                    teamId: sr.teamId,
                    omcId: sr.omcId,
                    ownedById: ownerId ? ownerId : user.id,
                    createdById: user.id,
                    offset: actionTemplate.offset,
                    timeSpent: actionTemplate.timeSpent
                });

                if (sr.plannedDate) {
                    const actionDt = DateHelper.addDays(sr.plannedDate, actionTemplate.offset);
                    if (!DateHelper.beforeToday(actionDt)) {
                        newAction.plannedDate = actionDt;
                    } else {
                        newAction.plannedDate = DateHelper.today();
                    }
                }

                sr.actions.push(newAction);
            }
        }
        return sr;
    }
}
